import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Calendar, Filter } from 'lucide-react';

/**
 * DashboardFilters - Pure Tailwind utilities with advanced filtering
 */
const DashboardFilters = ({
    currentFilters,
    onFilterChange,
    categories = [],
    showCategoryFilter = false,
    categoryLabel = 'Category',
    activeTab = 'overview'
}) => {
    const [showMore, setShowMore] = useState(false);

    const [countries, setCountries] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
                const response = await fetch(`${API_BASE_URL}/api/v1/locations/countries-only`);
                if (response.ok) {
                    const result = await response.json();
                    if (result.status === 'success' && Array.isArray(result.data)) {
                        setCountries(result.data);
                        const options = result.data.map(country => ({
                            value: country.name_en || country.name, // Use name as value for filtering
                            label: country.name_en || country.name
                        }));
                        // Sort alphabetically
                        options.sort((a, b) => a.label.localeCompare(b.label));
                        setCountryOptions(options);
                    }
                }
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        if (activeTab === 'users') {
            fetchCountries();
        }
    }, [activeTab]);

    if (!currentFilters) return null;

    const timeRanges = [
        { value: '7days', label: 'Last 7 Days' },
        { value: '30days', label: 'Last 30 Days' },
        { value: 'year', label: 'Last Year' },
        { value: 'all', label: 'All Time' },
        { value: 'custom', label: 'Custom' }
    ];

    const handleTimeRangeChange = (value) => {
        if (value === 'custom') {
            onFilterChange({ ...currentFilters, timeRange: value });
        } else {
            onFilterChange({ ...currentFilters, timeRange: value, start_date: '', end_date: '' });
        }
    };

    return (
        <div className="flex flex-col items-center w-full gap-4">
            {/* Time Range Tabs */}
            <div className="flex flex-wrap items-center justify-center border-b border-gray-100 w-full sm:w-auto overflow-x-auto no-scrollbar">
                <div className="flex items-center gap-4 md:gap-8 px-4 whitespace-nowrap">
                    {timeRanges.map(range => {
                        const isActive = currentFilters.timeRange === range.value;
                        return (
                            <button
                                key={range.value}
                                onClick={() => handleTimeRangeChange(range.value)}
                                className={`relative py-3 px-1 text-sm font-medium transition-all duration-200 ${isActive
                                    ? 'text-purple-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                {range.label}
                                {isActive && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 rounded-full" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Custom Date Range & Secondary Filters */}
            <div className="flex flex-wrap items-center justify-center gap-4 px-4 w-full">
                {currentFilters.timeRange === 'custom' && (
                    <div className="flex items-center gap-2 bg-gray-50/50 p-1.5 rounded-lg border border-gray-100">
                        <Calendar size={14} className="text-gray-400 ml-1" />
                        <input
                            type="date"
                            value={currentFilters.start_date || ''}
                            onChange={(e) => onFilterChange({ ...currentFilters, start_date: e.target.value })}
                            className="bg-transparent text-xs font-medium text-gray-700 outline-none border-none focus:ring-0 w-28"
                        />
                        <span className="text-gray-300">|</span>
                        <input
                            type="date"
                            value={currentFilters.end_date || ''}
                            onChange={(e) => onFilterChange({ ...currentFilters, end_date: e.target.value })}
                            className="bg-transparent text-xs font-medium text-gray-700 outline-none border-none focus:ring-0 w-28"
                        />
                    </div>
                )}

                <div className="flex items-center gap-4">
                    {showCategoryFilter && categories.length > 0 && (
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{categoryLabel}:</span>
                            <select
                                value={currentFilters.category || 'all'}
                                onChange={(e) => onFilterChange({ ...currentFilters, category: e.target.value })}
                                className="px-2 py-1 bg-transparent border-b border-gray-200 focus:border-purple-500 text-xs font-semibold text-gray-700 outline-none transition-colors cursor-pointer"
                            >
                                <option value="all">All</option>
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <button
                        onClick={() => setShowMore(!showMore)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border ${showMore
                            ? 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-200'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-purple-400'
                            }`}
                    >
                        <Filter size={12} />
                        {showMore ? 'Hide Filters' : 'Smart Filters'}
                        {showMore ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                </div>
            </div>

            {/* Expanded Metadata Filters */}
            {showMore && (
                <div className="w-full max-w-4xl bg-gray-50/50 rounded-2xl p-4 md:p-6 border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {activeTab === 'users' && (
                            <>
                                <FilterSelect
                                    label="Gender"
                                    value={currentFilters.gender || ''}
                                    options={[
                                        { value: 'male', label: 'Male' },
                                        { value: 'female', label: 'Female' }
                                    ]}
                                    onChange={(val) => onFilterChange({ ...currentFilters, gender: val })}
                                />
                                <FilterInput
                                    label="Min Age"
                                    type="number"
                                    value={currentFilters.min_age || ''}
                                    onChange={(val) => onFilterChange({ ...currentFilters, min_age: val })}
                                />
                                <FilterInput
                                    label="Max Age"
                                    type="number"
                                    value={currentFilters.max_age || ''}
                                    onChange={(val) => onFilterChange({ ...currentFilters, max_age: val })}
                                />
                                <FilterSelect
                                    label="Country"
                                    value={currentFilters.country || ''}
                                    options={countryOptions}
                                    onChange={(val) => onFilterChange({ ...currentFilters, country: val })}
                                />
                            </>
                        )}

                        {activeTab === 'social' && (
                            <>
                                <FilterInput
                                    label="Min Reactions"
                                    type="number"
                                    value={currentFilters.min_reactions || ''}
                                    onChange={(val) => onFilterChange({ ...currentFilters, min_reactions: val })}
                                />
                                <FilterInput
                                    label="Min Comments"
                                    type="number"
                                    value={currentFilters.min_comments || ''}
                                    onChange={(val) => onFilterChange({ ...currentFilters, min_comments: val })}
                                />
                            </>
                        )}

                        {activeTab === 'medical' && (
                            <>
                                <FilterSelect
                                    label="Visit Type"
                                    value={currentFilters.visit_type || ''}
                                    options={[
                                        { value: 'inpatient', label: 'Inpatient' },
                                        { value: 'outpatient', label: 'Outpatient' }
                                    ]}
                                    onChange={(val) => onFilterChange({ ...currentFilters, visit_type: val })}
                                />
                            </>
                        )}

                        <button
                            onClick={() => onFilterChange({ timeRange: '30days', category: 'all' })}
                            className="sm:col-span-2 lg:col-span-3 text-[10px] font-bold text-gray-400 hover:text-purple-600 uppercase tracking-widest transition-colors mt-2"
                        >
                            Reset All Filters
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const FilterSelect = ({ label, value, options, onChange }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{label}</label>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 bg-white rounded-xl border border-gray-100 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/5 text-sm font-medium text-gray-700 outline-none transition-all shadow-sm"
        >
            <option value="">All</option>
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

const FilterInput = ({ label, type, value, onChange }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{label}</label>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Any"
            className="w-full px-3 py-2 bg-white rounded-xl border border-gray-100 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/5 text-sm font-medium text-gray-700 outline-none transition-all shadow-sm"
        />
    </div>
);

export default DashboardFilters;
